import { createFileRoute, Link } from "@tanstack/react-router";
import { site } from "@/lib/site";

export const Route = createFileRoute("/popia")({
  head: () => ({
    meta: [
      { title: "POPIA Policy | Nkadimeng Danny Legal & Compliance Services" },
      {
        name: "description",
        content:
          "Our Protection of Personal Information Act (POPIA) policy — how we lawfully process personal information and uphold the eight conditions for lawful processing.",
      },
      { property: "og:title", content: "POPIA Policy" },
      {
        property: "og:description",
        content: "How we uphold POPIA's eight conditions for lawful processing.",
      },
    ],
  }),
  component: PopiaPage,
});

function PopiaPage() {
  const conditions = [
    ["Accountability", "We are responsible for ensuring all eight conditions are met."],
    ["Processing limitation", "Information is processed lawfully, minimally, and with consent or another lawful basis."],
    ["Purpose specification", "Collected for a specific, explicitly defined and lawful purpose."],
    ["Further processing limitation", "Compatible with the original purpose only."],
    ["Information quality", "Reasonable steps to ensure data is complete, accurate and up to date."],
    ["Openness", "We document our processing and notify you when collecting data."],
    ["Security safeguards", "Appropriate, reasonable technical and organisational measures."],
    ["Data subject participation", "You may access, correct or delete your information."],
  ] as const;

  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-16 md:py-20">
          <span className="gold-rule" />
          <h1 className="font-display text-4xl text-navy md:text-5xl">
            POPIA Policy
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
            Our commitment to the Protection of Personal Information Act, 4
            of 2013.
          </p>
        </div>
      </section>

      <section className="container-narrow py-12 md:py-16 space-y-8 text-sm md:text-base leading-relaxed">
        <p className="text-muted-foreground">
          Nkadimeng Danny Legal &amp; Compliance Services acts as the
          responsible party under POPIA. We process personal information in
          accordance with the eight conditions for lawful processing set out
          in Chapter 3 of the Act.
        </p>

        <div className="grid gap-4 md:grid-cols-2">
          {conditions.map(([title, body]) => (
            <div key={title} className="rounded-xl border border-border bg-card p-5 shadow-soft">
              <h3 className="font-display text-lg text-navy">{title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{body}</p>
            </div>
          ))}
        </div>

        <div>
          <h2 className="font-display text-2xl text-navy">Information Officer</h2>
          <p className="mt-3 text-muted-foreground">
            Nkadimeng Danny serves as the Information Officer. Requests under
            POPIA or the Promotion of Access to Information Act (PAIA) may
            be directed to{" "}
            <a href={`mailto:${site.email}`} className="text-navy underline hover:text-gold">
              {site.email}
            </a>
            .
          </p>
        </div>

        <div>
          <h2 className="font-display text-2xl text-navy">Special personal information</h2>
          <p className="mt-3 text-muted-foreground">
            We only process special categories of personal information (such
            as health or criminal record information) with your express
            consent or where authorised by law, and only to the extent
            necessary to assist with your matter.
          </p>
        </div>

        <div>
          <h2 className="font-display text-2xl text-navy">Information Regulator</h2>
          <p className="mt-3 text-muted-foreground">
            If you believe we have not handled your personal information
            lawfully, you may lodge a complaint with the Information
            Regulator (South Africa) at{" "}
            <a
              href="https://inforegulator.org.za"
              target="_blank"
              rel="noreferrer"
              className="text-navy underline hover:text-gold"
            >
              inforegulator.org.za
            </a>
            .
          </p>
        </div>

        <p className="text-sm text-muted-foreground">
          Read alongside our{" "}
          <Link to="/privacy" className="text-navy underline hover:text-gold">
            Privacy Policy
          </Link>{" "}
          and{" "}
          <Link to="/terms" className="text-navy underline hover:text-gold">
            Terms of Service
          </Link>
          .
        </p>
      </section>
    </>
  );
}
